/**
 * Shared pieces for the Fanatic sign-in and join pages.
 *
 * Both pages need the same three things — the page chrome, the social buttons,
 * and the "already have an account?" cross-link — and they must stay visually
 * identical, because a fan bouncing between them should feel like they are on
 * one surface rather than two apps.
 *
 * Social buttons are rendered from `GET /auth/v1/fan/sign-in-options`, never a
 * hardcoded list. Every consumer provider ships DISABLED until its Keycloak
 * broker is configured, so the honest default is zero buttons and a working
 * password form. A hardcoded list would render buttons that cannot complete a
 * login, which is the one failure a fan will not forgive.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import type { FanSignInOptions } from '../../lib/api-types';
import { ROUTES } from '../../lib/constants';

/**
 * Load the fan sign-in options once.
 *
 * A failure here is deliberately NOT surfaced as a page error: the password
 * form works without it, so a fan should still be able to sign in when the
 * options call fails. They simply see no social buttons.
 */
export function useFanSignInOptions(): {
  options: FanSignInOptions | null;
  isLoading: boolean;
} {
  const apiClient = useApiClient();
  const [options, setOptions] = useState<FanSignInOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;
    apiClient
      .getFanSignInOptions(controller.signal)
      .then((result) => {
        if (isActive) setOptions(result);
      })
      .catch(() => {
        // Degrade to password-only; see the note above.
        if (isActive) setOptions(null);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [apiClient]);

  return { options, isLoading };
}

export function FanAuthChrome({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="auth">
      <Link to={ROUTES.landing} className="auth__brand">
        <img src="/images/logo-mark.png" alt="" width={32} height={32} />
        <span>
          GRIDIRON <em>TRACKER</em>
        </span>
      </Link>

      <main className="auth__panel">
        <div className="fan-auth__head">
          <span className="badge">For fans &amp; families</span>
          <h1 className="auth__title">{title}</h1>
          <p className="muted">{subtitle}</p>
        </div>
        {children}
      </main>
    </div>
  );
}

/**
 * Social sign-in buttons.
 *
 * Renders nothing at all when no provider is enabled — not an empty state, not
 * a disabled button, not a divider. A fan should never see an affordance that
 * cannot work, and the password form above it is a complete path on its own.
 */
export function FanSocialButtons({
  options,
  redirectUri,
  verb,
  isBusy,
}: {
  options: FanSignInOptions | null;
  redirectUri: string;
  /** "Continue" reads correctly for both signing in and joining. */
  verb?: string;
  isBusy?: boolean;
}): JSX.Element | null {
  const apiClient = useApiClient();
  if (options === null || options.providers.length === 0) return null;

  return (
    <>
      <div className="fan-auth__divider" role="separator">
        <span>or</span>
      </div>
      <div className="stack stack--tight">
        {options.providers.map((provider) => (
          <a
            key={provider.slug}
            className="btn btn--secondary btn--block"
            aria-disabled={isBusy === true}
            href={apiClient.oauthAuthorizeUrl(provider.slug, options.realm_tenant_id, redirectUri)}
          >
            {verb ?? 'Continue'} with {provider.display_name}
          </a>
        ))}
      </div>
    </>
  );
}

/** Cross-link between the two fan pages (F6: sign in and join must be distinct). */
export function FanAuthSwitch({ mode }: { mode: 'login' | 'signup' }): JSX.Element {
  return mode === 'login' ? (
    <p className="auth__alt">
      New here? <Link to={ROUTES.fanSignup}>Create an account</Link>
    </p>
  ) : (
    <p className="auth__alt">
      Already have an account? <Link to={ROUTES.fanLogin}>Sign in</Link>
    </p>
  );
}

/** Fans and staff are separate doors; each page points at the other. */
export function StaffAuthHint(): JSX.Element {
  return (
    <p className="auth__alt muted">
      Coach or team staff? <Link to={ROUTES.login}>Sign in to your team portal</Link>
    </p>
  );
}
